import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, finalize, map, mergeMap, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { User } from '../models/login/user';
import { Router } from '@angular/router';
import { SignInResponse } from '../models/login/sign-in-response';
import { from } from "linq-to-typescript";
import { PermissionGroupItem, UserRoleMapping } from '../models/user-management/permission-group';
import { UserManagementService } from './user-management/user-management.service';
import { Action } from '../models/action';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isLoading$: Observable<boolean>;
  isLoadingSubject: BehaviorSubject<boolean>;
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  currentUserSubject: BehaviorSubject<User | null>;
  currentUser$: Observable<User | null>;

  userRoleMappingSubject: BehaviorSubject<UserRoleMapping | null>;
  userRoleMapping$: Observable<UserRoleMapping | null>;

  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  get currentUserValue(): User {
    return this.currentUserSubject.value!;
  }

  set currentUserValue(user: User) {
    this.currentUserSubject.next(user);
  }

  constructor(private http: HttpClient, private router: Router, private userManagementService: UserManagementService) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.currentUserSubject = new BehaviorSubject<User | null>(null);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.userRoleMappingSubject = new BehaviorSubject<UserRoleMapping | null>(null);
    this.userRoleMapping$ = this.userRoleMappingSubject.asObservable();
  }

  decryptToken(token: string): Observable<SignInResponse> {
    this.isLoadingSubject.next(true);
    return this.http
      .post<any>(`${environment.authApi}/Auth/decrypt`, { authToken: token }, { headers: this.headers })
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  getUserByToken(): Observable<User | undefined> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.accessToken) {
      return of(undefined);
    }
    this.isLoadingSubject.next(true);
    let userprofile = this.http.get(`${environment.authApi}/Auth/me`).pipe(
      mergeMap((profile: User) => {
        if (profile) {
          return combineLatest([
            of(profile),
            this.userManagementService.getUserRoleMappingByUserId(profile.id)]
          )
        } else { this.logout(); }
      }),
      map(([user, userRoleMapping]) => {
        this.currentUserSubject = new BehaviorSubject<User | null>(user);
        this.userRoleMappingSubject = new BehaviorSubject<UserRoleMapping | null>(userRoleMapping);
        this.isLoadingSubject.next(false);
        return user;
      })
    )
    return userprofile;

  }

  refreshToken(): Observable<SignInResponse> {
    return this.http.post<SignInResponse>(
      `${environment.authApi}/auth/refresh-token`,
      {
        headers: this.headers,
      }
    );
  }

  setAuthFromLocalStorage(auth: SignInResponse): boolean {
    if (auth && auth.accessToken) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  private getAuthFromLocalStorage(): SignInResponse | undefined {
    try {
      const authData = JSON.parse(localStorage.getItem(this.authLocalStorageToken)!);
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  logout() {
    localStorage.removeItem(this.authLocalStorageToken);
    window.close();
  }

  isAllowedToGrantSystem(mstSystemId: number): boolean {
    if (this.userRoleMappingSubject.value) {
      let isAllowed = from<PermissionGroupItem>(this.userRoleMappingSubject.value?.permissionGroup?.permissionGroupItems)
        .any((a) => a?.mstSubsystem?.mstSystemId === mstSystemId);
      return isAllowed;
    }
  }

  isAllowedToGrantSubsystem(mstSystemId: number, mstSubsystemId: number): boolean {
    if (this.userRoleMappingSubject.value) {
      let isAllowed = from<PermissionGroupItem>(this.userRoleMappingSubject.value?.permissionGroup?.permissionGroupItems)
        .any((a) => a?.mstSubsystemId === mstSubsystemId && a?.mstSubsystem?.mstSystemId === mstSystemId);
      return isAllowed;
    }
  }

  get isAdmin(): boolean { return this.currentUserSubject.value.username == 'testuser_admin' }

  get isAllowedToSeeInActiveInfo(): boolean {
    if (this.userRoleMappingSubject.value) {
      let isAllowed = from<PermissionGroupItem>(this.userRoleMappingSubject.value?.permissionGroup?.permissionGroupItems)
        .any((a) => a?.mstActionId === 5);
      return isAllowed;
    }
    else {
      return false;
    }
  }


  isAllowedToAction(mstSubsystemId: number): Action {
    if (this.userRoleMappingSubject.value) {
      return {
        canView:
          from<PermissionGroupItem>(this.userRoleMappingSubject.value?.permissionGroup?.permissionGroupItems)
            .any((a) => a?.mstSubsystemId === mstSubsystemId && a?.mstActionId === 1),

        canCreate:
          from<PermissionGroupItem>(this.userRoleMappingSubject.value?.permissionGroup?.permissionGroupItems)
            .any((a) => a?.mstSubsystemId === mstSubsystemId && a?.mstActionId === 2),

        canDelete:
          from<PermissionGroupItem>(this.userRoleMappingSubject.value?.permissionGroup?.permissionGroupItems)
            .any((a) => a?.mstSubsystemId === mstSubsystemId && a?.mstActionId === 3),

        canUpdate:
          from<PermissionGroupItem>(this.userRoleMappingSubject.value?.permissionGroup?.permissionGroupItems)
            .any((a) => a?.mstSubsystemId === mstSubsystemId && a?.mstActionId === 4),

        canReport:
          from<PermissionGroupItem>(this.userRoleMappingSubject.value?.permissionGroup?.permissionGroupItems)
            .any((a) => a?.mstSubsystemId === mstSubsystemId && a?.mstActionId === 5),

        canApprove: from<PermissionGroupItem>(this.userRoleMappingSubject.value?.permissionGroup?.permissionGroupItems)
          .any((a) => a?.mstSubsystemId === mstSubsystemId && a?.mstActionId === 6)

      } as Action;
    }
  }
}